import { Component, EventEmitter, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { DialogComponent } from '@app/components';
import { ConfirmationDialogComponent } from '@app/components/platform/confirmation-dialog/confirmation-dialog.component';
import { AnalyticEvents } from '@app/enums';
import { getMappingTableColumnTypes } from '@app/modules/import/functions/get-mapping-table-column-types';
import { originalKeyValueOrder } from '@app/modules/import/functions/original-key-value-order';
import { ColumnRequiredService } from '@app/modules/import/services/column-required.service';
import { EmployeeHumiColumnService } from '@app/modules/import/services/column-type/employee-humi-column.service';
import { MappingTables } from '@app/modules/import/types/mapping-tables.types';
import { AnalyticService, AuthService, NotifyService } from '@app/services';
import { BaseForm } from '@forms/base.form';
import { FieldCompactForm } from '@forms/settings/components';
import { Column } from '@import/classes';
import { MappingTableSection } from '@import/components';
import { customFieldLocations, fieldLocationToComponent } from '@import/enums/custom-field-locations.enum';
import { addDataFieldsToMappingTables } from '@import/functions/add-data-fields-mapping-tables.function';
import { moveJobAndPayBankingFieldsToDefaultRequired } from '@import/functions/move-job-and-pay-banking-fields-to-default-required';
import { convertToBase26Alpha } from '@import/functions/convert-to-base-26-alpha';
import { ColumnType } from '@import/interfaces';
import { CustomFieldsService, EmployeeRowAndColumnService, HumiColumnSmartMatchingService } from '@import/services';
import { MappingTableTypes } from '@import/types/mapping-table.types';
import { DataField } from '@models/settings/data-field.model';
import { DataFieldTypes } from '@app/enums/data-field-types.enum';

@Component({
    selector: 'app-form-import-employee-column-map',
    templateUrl: './map-columns.template.html',
    styleUrls: ['./map-columns.style.scss'],
})
/**
 * User columns to system columns mapping step
 *
 * Refactor notes: A lot fo the values in here at the moment pertain specifically to employee importing.
 * Once a second importer is being worked on, generalize the pattern of new column creation adn the links and tooltips and descriptions
 * and all that into the service or the parent view, depending on which makes more sense a the time.
 */
export class MapColumnsForm extends BaseForm implements OnInit {
    @ViewChild('customFieldModal', { static: true }) customFieldModal: DialogComponent;
    @ViewChild('remainingColumnConfirmation', { static: true })
    remainingColumnConfirmation: ConfirmationDialogComponent;
    @ViewChild('customFieldForm', { static: true }) customFieldForm: FieldCompactForm;
    @ViewChildren('tableSections') tableSections: QueryList<MappingTableSection>;

    @Output() isLoading: EventEmitter<boolean> = new EventEmitter<boolean>();

    showModalLoader = false;
    allRequiredFieldsAssigned: boolean;
    remainingColumnCount = 0;
    convertToBase26Alpha = convertToBase26Alpha;
    mappingTables: MappingTables;
    mappingTablesColumnTypes: ColumnType[];
    originalKeyValueOrder = originalKeyValueOrder;

    constructor(
        public columnRequiredService: ColumnRequiredService,
        public humiColumnService: EmployeeHumiColumnService,
        public importerService: EmployeeRowAndColumnService,
        public smartMatchingService: HumiColumnSmartMatchingService,
        protected auth: AuthService,
        protected notify: NotifyService,
        protected analytics: AnalyticService,
        protected customFieldService: CustomFieldsService
    ) {
        super();
        this.mappingTables = this.humiColumnService.getMappingTables();
    }

    async ngOnInit(): Promise<void> {
        this.isLoading.emit(true);

        try {
            await this.humiColumnService.setDataFieldsColumns();
        } catch (err) {
            this.notify.error(err || 'Unable to retrieve Custom Field Columns.');
        }

        this.mappingTablesColumnTypes = getMappingTableColumnTypes(this.mappingTables);

        try {
            await this.humiColumnService.setDynamicOptionsForColumnTypes(this.mappingTablesColumnTypes);
        } catch (err) {
            this.notify.error(err || 'Unable to retrieve dynamic options for columns.');
        }

        if (this.importerService.shouldSmartMatchColumns()) {
            this.smartMatchingService.matchCsvToHumiColumns(
                this.mappingTablesColumnTypes,
                this.importerService.columns
            );
        }

        // Check Table Section Validity on Page Load
        this.tableSections.forEach((tableSection: MappingTableSection) => tableSection.setIsSectionValid());
        this.checkAllRequiredHumiColumnsAreAssigned();

        this.isLoading.emit(false);
    }

    compareColumns(a: Column, b: Column): boolean {
        return a.name === b.name;
    }

    openCustomFieldModal(fieldLocation: MappingTableTypes): void {
        this.customFieldForm.applyComponentFilter(fieldLocationToComponent[fieldLocation]).reset();
        this.customFieldModal.open = true;
    }

    async onSaveCustomField(form: FieldCompactForm): Promise<void> {
        if (!form.valid) {
            return;
        }

        if (form.field.type === DataFieldTypes.dropdown && form.dataFieldOptionState.getSavableOptions().length === 0) {
            this.notify.error('import.cannotSaveDropdownWithoutOptions');
            return;
        }

        const field: DataField = form.field;

        this.isLoading.emit(true);
        this.showModalLoader = true;

        try {
            const [dataFieldGroup] = await Promise.all([this.customFieldService.saveFieldGroup(form.group)]);

            field.dataFieldGroupId = dataFieldGroup.id;

            await field.param('company', this.auth.company.id).save();

            if (form.field.type === DataFieldTypes.dropdown) {
                await form.dataFieldOptionState.save();
            }

            const savedField = await DataField.param('company', this.auth.company.id)
                .with(['dataFieldGroup', 'dataFieldOptions'])
                .find(field.id);

            addDataFieldsToMappingTables(
                [savedField],
                this.humiColumnService.getMappingTables(),
                customFieldLocations[dataFieldGroup.component]
            );

            this.analytics.trackEvent(AnalyticEvents.ImporterCustomFieldCreated, {
                customFieldType: savedField.type,
            });
        } catch (err) {
            this.notify.error(err || 'Unable to create custom field');
        }

        this.customFieldModal.open = false;
        this.isLoading.emit(false);
        this.showModalLoader = false;
        setTimeout(() => form.reset(), 200);
    }

    onSubmit(): void {
        if (!this.form.valid) {
            this.notify.error('import.requiredFieldsNotAllMatched');
        }
    }

    checkAllRequiredHumiColumnsAreAssigned(): void {
        this.mappingTablesColumnTypes = getMappingTableColumnTypes(this.humiColumnService.getMappingTables());
        this.columnRequiredService.setConditionallyRequiredColumnTypes(this.mappingTablesColumnTypes);
        this.remainingColumnCount = this.humiColumnService.getUnmatchedColumnItems(this.importerService.columns).length;
        const hasRequiredColumnsWithoutMapping = this.mappingTablesColumnTypes
            .filter((humiColumn: ColumnType) => humiColumn.required || humiColumn.conditionallyRequired)
            .some((humiColumn: ColumnType) => !humiColumn.csvColumn);
        this.allRequiredFieldsAssigned = hasRequiredColumnsWithoutMapping ? undefined : true;

        /*
         * Check if bankAccounts & paymentMethod are required by company settings,
         * if so we'll append those fields to default required.
         * */
        const { bankAccounts, paymentMethod } = this.columnRequiredService.columnRequirementConfig;
        if (bankAccounts && paymentMethod && this.mappingTables.jobAndPay.sections.banking) {
            this.mappingTables = moveJobAndPayBankingFieldsToDefaultRequired(this.mappingTables);
        }
    }
}
